import { Fragment, useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthContext";
import { useFleetApi, type ActivityRow } from "./api";

// Dot color by agent name heuristic
function agentDot(agent: string): "w" | "o" | "s" {
  const a = agent.toLowerCase();
  if (a.includes("sentinel") || a.includes("watcher") || a.includes("drift") || a.includes("market") || a.includes("feed")) return "w";
  if (a.includes("detective") || a.includes("investigator")) return "s";
  return "o";
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

// Date-range filters
const NOW = () => Date.now();
const FILTERS: { label: string; key: string; fn: (d: Date) => boolean }[] = [
  { label: "All", key: "all", fn: () => true },
  {
    label: "Today",
    key: "today",
    fn: d => {
      const t = new Date();
      return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
    },
  },
  { label: "Last 24h", key: "24h", fn: d => NOW() - d.getTime() < 86400000 },
  { label: "Last 7d", key: "7d", fn: d => NOW() - d.getTime() < 7 * 86400000 },
];

// Row-type pill colours
function rowTypePill(rt: ActivityRow["row_type"]): string {
  if (rt === "audit") return "muted";
  if (rt === "action") return "ok";
  return "warn"; // escalation
}

// Map escalation status -> pill colour
function statusPill(status: string | null | undefined): string {
  if (!status) return "muted";
  if (status === "resolved") return "ok";
  if (status === "open") return "warn";
  if (status === "overridden") return "info";
  return "muted";
}

function eventBody(r: ActivityRow) {
  if (r.row_type === "audit") {
    return (
      <>
        <span className={`pill ${rowTypePill(r.row_type)}`}>audit</span>
        {r.kind ? <> {r.kind}</> : null}
        {r.tool ? <span style={{ color: "var(--text-2)" }}> · {r.tool}</span> : null}
        {typeof r.latency_ms === "number" ? (
          <span style={{ color: "var(--text-3)", marginLeft: "6px" }}>
            {r.latency_ms}ms
          </span>
        ) : null}
      </>
    );
  }
  if (r.row_type === "action") {
    return (
      <>
        <span className={`pill ${rowTypePill(r.row_type)}`}>action</span>
        {r.kind ? <> {r.kind}</> : null}
        {r.subject ? <span style={{ color: "var(--text-2)" }}> · {r.subject}</span> : null}
        {r.reverted_at ? (
          <span className="pill crit" style={{ marginLeft: "6px" }}>reverted</span>
        ) : null}
      </>
    );
  }
  // escalation
  return (
    <>
      <span className={`pill ${rowTypePill(r.row_type)}`}>escalation</span>
      {r.situation_class ? <> {r.situation_class}</> : null}
      {r.decision_class ? (
        <span style={{ color: "var(--text-2)" }}> → {r.decision_class}</span>
      ) : null}
      {r.status ? (
        <span className={`pill ${statusPill(r.status)}`} style={{ marginLeft: "6px" }}>
          {r.status}
        </span>
      ) : null}
    </>
  );
}

export default function Activity() {
  const { token, logout } = useAuth();
  const { fetchActivity } = useFleetApi(token, logout);

  const [rows, setRows] = useState<ActivityRow[] | null>(null);
  const [counts, setCounts] = useState<{ audit: number; action: number; escalation: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ActivityRow["row_type"] | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    fetchActivity({ limit: 200 })
      .then(r => {
        if (alive) {
          setRows(r.timeline);
          setCounts(r.counts);
        }
      })
      .catch(e => { if (alive) setErr(String(e)); });
    return () => { alive = false; };
  }, [fetchActivity]);

  // Unique agents for filter chips
  const agents = useMemo(() => {
    if (!rows) return [];
    return Array.from(new Set(rows.map(r => r.agent))).sort();
  }, [rows]);

  // Apply filters
  const filtered = useMemo(() => {
    if (!rows) return [];
    const tf = FILTERS.find(f => f.key === timeFilter) ?? FILTERS[0];
    return rows.filter(r => {
      const d = new Date(r.created_at);
      if (!tf.fn(d)) return false;
      if (agentFilter && r.agent !== agentFilter) return false;
      if (typeFilter && r.row_type !== typeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const haystack = [
          r.trace_id,
          r.agent,
          r.kind ?? "",
          r.tool ?? "",
          r.subject ?? "",
          r.situation_class ?? "",
          r.decision_class ?? "",
          r.situation_key ?? "",
        ].join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [rows, timeFilter, agentFilter, typeFilter, search]);

  return (
    <section>
      <p className="page-lede">
        {rows == null
          ? "Loading activity…"
          : counts
            ? `Live · ${filtered.length} shown · ${counts.audit} audit / ${counts.action} action / ${counts.escalation} escalation.`
            : `Live · ${filtered.length} shown.`}{" "}
        Unified timeline of audit pings, side-effect actions, and escalations. Click a trace to drill in.
      </p>

      {err && <p style={{ color: "var(--crit)", fontSize: "12.5px" }}>{err}</p>}

      <div className="chips">
        {FILTERS.map(f => (
          <span
            key={f.key}
            className={`chip${timeFilter === f.key ? " active" : ""}`}
            onClick={() => setTimeFilter(f.key)}
          >
            {f.label}
          </span>
        ))}

        <span style={{ marginLeft: "14px", color: "var(--text-3)", fontSize: "11.5px", alignSelf: "center" }}>
          TYPE:
        </span>
        {(["audit", "action", "escalation"] as const).map(t => (
          <span
            key={t}
            className={`chip${typeFilter === t ? " active" : ""}`}
            onClick={() => setTypeFilter(prev => (prev === t ? null : t))}
          >
            {t}
          </span>
        ))}

        {agents.length > 0 && (
          <span style={{ marginLeft: "14px", color: "var(--text-3)", fontSize: "11.5px", alignSelf: "center" }}>
            AGENT:
          </span>
        )}
        {agents.map(a => (
          <span
            key={a}
            className={`chip${agentFilter === a ? " active" : ""}`}
            onClick={() => setAgentFilter(prev => (prev === a ? null : a))}
          >
            {a.replace(/_/g, " ")}
          </span>
        ))}

        <span style={{ marginLeft: "auto" }}>
          <input
            className="search"
            placeholder="search trace_id, agent, kind, subject..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </span>
      </div>

      <div className="card">
        <div className="feed">
          <div className="col">Time</div>
          <div className="col">Agent</div>
          <div className="col">Event</div>
          <div className="col">Trace</div>

          {rows == null && (
            <div className="col" style={{ gridColumn: "1 / -1", color: "var(--text-3)", padding: "12px 0" }}>
              Loading…
            </div>
          )}

          {rows && filtered.length === 0 && (
            <div className="col" style={{ gridColumn: "1 / -1", color: "var(--text-3)", padding: "12px 0" }}>
              No events match the current filters.
            </div>
          )}

          {filtered.map(r => {
            const dot = agentDot(r.agent);
            const key = `${r.row_type}:${r.id}`;
            return (
              <Fragment key={key}>
                <div className="col t">{fmtTime(r.created_at)}</div>
                <div className="col actor">
                  <span className={`dot ${dot}`}></span>
                  {r.agent.replace(/_/g, " ")}
                </div>
                <div className="col body">{eventBody(r)}</div>
                <div className="col right">
                  <a href={`/fleet/trace?id=${r.trace_id}`}>{r.trace_id}</a>
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
}
